'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, InputHTMLAttributes, useRef } from 'react';

import { cn } from '../../libs/utils';
import { semanticColor } from '../../theme/semanticColor';
import { Svg } from '../common';

const searchVariants = cva(
  'flex items-center gap-normal-small container-padding-small rounded-bzc-circular border border-neutral-border-main bg-neutral-background-low focus-within:border-neutral-border-highest',
  {
    variants: {
      size: {
        medium: 'h-[48px]',
        small: 'h-[38px]'
      }
    },
    defaultVariants: {
      size: 'medium'
    }
  }
);

const INPUT_FONT_CLASS = {
  medium: 'body-medium-medium',
  small: 'body-small-medium'
} as const;

export interface SearchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange' | 'size'>, VariantProps<typeof searchVariants> {
  value: string;
  onChange: (value: string) => void;
}

export const Search = forwardRef<HTMLInputElement, SearchProps>(({ className, placeholder, value, onChange, size = 'medium', ...props }, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleClear = () => {
    onChange('');
    inputRef.current?.focus();
  };

  const setRefs = (node: HTMLInputElement | null) => {
    inputRef.current = node;

    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  const inputFontClass = INPUT_FONT_CLASS[size ?? 'medium'];

  return (
    <div className={cn(searchVariants({ size, className }))}>
      <div className="shrink-0 flex items-center justify-center">
        <Svg src="ic_mag.svg" width={22} height={22} color={semanticColor.neutral.foreground.lowest} />
      </div>

      <input
        ref={setRefs}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        className={cn(
          inputFontClass,
          'w-full bg-transparent text-neutral-foreground-main placeholder:text-neutral-foreground-lowest focus:outline-none truncate'
        )}
        {...props}
      />

      {value.length > 0 && (
        <button type="button" onClick={handleClear} aria-label="검색어 지우기" className="shrink-0 flex items-center justify-center cursor-pointer">
          <Svg src="ic_x-filled.svg" width={20} height={20} color={semanticColor.neutral.foreground.lowest} />
        </button>
      )}
    </div>
  );
});

Search.displayName = 'Search';
